import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Context dla uprawnień użytkownika
const PermissionsContext = createContext(null);

// Provider uprawnień
export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState(null); // Uprawnienia per lokalizacja
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedForUserId, setLoadedForUserId] = useState(null); // Dla którego usera załadowano

  // Pobierz uprawnienia użytkownika
  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/permissions/my');

      const data = response.data;
      const permissionsData = data.data || [];

      // Przekształć na mapę location_id -> permissions
      const permissionsMap = {};
      permissionsData.forEach(loc => {
      permissionsMap[loc.location_id] = {
        permissions: loc.permissions || {},
        locationName: loc.location_nazwa,
        locationCode: loc.kod_lokalizacji,
        rola: loc.rola,
        isAdmin: loc.is_admin
      };

      // Jeśli to admin, ustaw flagę
      if (loc.is_admin) {
        setIsAdmin(true);
      }
      });

      setPermissions(permissionsMap);
      setLoadedForUserId(user?.user_id); // Zapamiętaj dla którego usera

      // Ustaw pierwszą lokalizację jako domyślną jeśli nie ma wybranej.
      // Funkcyjny setState — nie czytamy currentLocationId, dzieki czemu
      // fetchPermissions NIE zalezy od currentLocationId. Wczesniej ta zaleznosc
      // powodowala podwojne pobranie /permissions/my przy kazdym logowaniu
      // (fetch -> ustawia currentLocationId -> zmiana deps -> ponowny fetch).
      if (permissionsData.length > 0) {
        setCurrentLocationId(prev => prev || permissionsData[0].location_id);
      }
    } catch (err) {
      console.error('Błąd pobierania uprawnień:', err);
      setError(err.message);
      setPermissions({});
      // WAŻNE: bez tego loadedForUserId zostaje null, permissionsNotLoadedForCurrentUser
      // jest true NA STAŁE i effectiveLoading nigdy nie wraca do false. Skutek:
      // ProtectedRoute wisi na "Sprawdzanie uprawnień..." w nieskończoność, a panele
      // gatowane flagą `loading` (np. import faktur) znikają bez komunikatu.
      // Nieudany fetch = uprawnienia PUSTE (domyślnie odmowa), ale ZAŁADOWANE.
      setLoadedForUserId(user?.user_id);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  // Pobierz uprawnienia przy starcie i po zmianie użytkownika (login/logout)
  useEffect(() => {
    // Poczekaj aż AuthContext skończy sprawdzanie sesji
    if (authLoading) {
      return;
    }

    // Reset przy wylogowaniu
    if (!isAuthenticated) {
      setPermissions({});
      setIsAdmin(false);
      setLoading(false);
      setLoadedForUserId(null);
      return;
    }
    // WAŻNE: Ustaw loading SYNCHRONICZNIE przed async fetch
    // To zapewnia że PosPage widzi loading=true od razu po zalogowaniu
    setLoading(true);
    fetchPermissions();
  }, [authLoading, isAuthenticated, user?.user_id, fetchPermissions]);

  // Sprawdź pojedyncze uprawnienie
  const hasPermission = useCallback((module, action = 'dostep', locationId = null) => {
    // Admin zawsze ma wszystko
    if (isAdmin) return true;

    // Użyj podanej lokalizacji lub bieżącej
    const locId = locationId || currentLocationId;

    if (!locId || !permissions) return false;

    const locPerms = permissions[locId];
    if (!locPerms) return false;

    const modulePerms = locPerms.permissions?.[module];
    if (!modulePerms) return false;

    // Sprawdź czy ma dostęp do modułu
    if (!modulePerms.dostep) return false;

    // Jeśli pytamy tylko o dostęp, zwróć true
    if (action === 'dostep') return true;

    // Sprawdź konkretną akcję
    return modulePerms[action] === true;
  }, [permissions, currentLocationId, isAdmin]);

  // Sprawdź wiele uprawnień naraz (AND)
  const hasAllPermissions = useCallback((checks, locationId = null) => {
    return checks.every(([module, action]) => hasPermission(module, action, locationId));
  }, [hasPermission]);

  // Sprawdź wiele uprawnień naraz (OR)
  const hasAnyPermission = useCallback((checks, locationId = null) => {
    return checks.some(([module, action]) => hasPermission(module, action, locationId));
  }, [hasPermission]);

  // Pobierz wszystkie uprawnienia dla lokalizacji
  const getLocationPermissions = useCallback((locationId = null) => {
    const locId = locationId || currentLocationId;
    if (!locId || !permissions) return null;
    return permissions[locId]?.permissions || null;
  }, [permissions, currentLocationId]);

  // Pobierz listę dostępnych lokalizacji
  const getAvailableLocations = useCallback(() => {
    if (!permissions) return [];
    return Object.entries(permissions).map(([id, data]) => ({
      id: parseInt(id),
      name: data.locationName,
      code: data.locationCode,
      rola: data.rola
    }));
  }, [permissions]);

  // Zmień bieżącą lokalizację
  const changeLocation = useCallback((locationId) => {
    setCurrentLocationId(locationId);
  }, []);

  // Odśwież uprawnienia
  const refreshPermissions = useCallback(() => {
    return fetchPermissions();
  }, [fetchPermissions]);

  // Computed loading - true jeśli:
  // 1. Auth się ładuje LUB
  // 2. Aktualnie trwa fetch uprawnień LUB
  // 3. Jesteśmy zalogowani ale uprawnienia nie zostały jeszcze załadowane dla TEGO usera
  // 4. Mamy usera w localStorage ale AuthContext jeszcze nie zaktualizował stanu (transitional state)
  const permissionsNotLoadedForCurrentUser = isAuthenticated && loadedForUserId !== user?.user_id;
  // BEZPIECZENSTWO: Usuniety transitionalState oparty na localStorage (podatny na XSS).
  // Auth state pochodzi wylacznie z httpOnly cookie session via AuthContext.
  const effectiveLoading = authLoading || loading || permissionsNotLoadedForCurrentUser;

  const value = {
    // State
    permissions,
    currentLocationId,
    isAdmin,
    loading: effectiveLoading,
    error,

    // Metody sprawdzania
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getLocationPermissions,
    getAvailableLocations,

    // Akcje
    changeLocation,
    refreshPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Hook do używania uprawnień
export const usePermissions = () => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};

// Komponent warunkowego renderowania na podstawie uprawnień
export const PermissionGate = ({
  module,
  action = 'dostep',
  locationId = null,
  fallback = null,
  children
}) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return null; // lub spinner
  }

  if (!hasPermission(module, action, locationId)) {
    return fallback;
  }

  return children;
};

// HOC do ochrony komponentów
export const withPermission = (WrappedComponent, module, action = 'dostep') => {
  return function PermissionProtectedComponent(props) {
    const { hasPermission, loading } = usePermissions();

    if (loading) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Sprawdzanie uprawnień...
        </div>
      );
    }

    if (!hasPermission(module, action)) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '0.5rem',
          margin: '1rem'
        }}>
          <h3>Brak uprawnień</h3>
          <p>Nie masz uprawnień do tej funkcji.</p>
          <p>Wymagane: {module}.{action}</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default PermissionsContext;
